import {
    Material,
    Primitive,
    GeometryInstance,
    PolylineVolumeGeometry,
    VertexFormat,
    Cartesian3,
    Cartesian2,
    Math as  CesiumMath,
    MaterialAppearance,
    TextureMagnificationFilter,
    TextureMinificationFilter,
    Matrix4,
    ShadowMode
} from 'cesium';
import transformRgb from "../_util/transformRgb";

const glsl = `
  czm_material czm_getMaterial(czm_materialInput materialInput) {
    czm_material material = czm_getDefaultMaterial(materialInput);
    vec2 st = materialInput.st;
    // vec2 st = materialInput.st * repeat;
    vec4 colorImage = texture2D(image, vec2(fract((st.s - speed * czm_frameNumber * 0.001)), st.t));
    material.alpha = colorImage.a;
    material.diffuse = colorImage.rgb;
    return material;
  }`;

// 创建圆形
function computeCircle(radius) {
    const positions = [];
    for (let i = 0; i < 360; i++) {
        const radians = CesiumMath.toRadians(i);
        positions.push(new Cartesian2(radius * Math.cos(radians), radius * Math.sin(radians)));
    }
    return positions;
}

// 创建方形
function computeSquare(size) {
    return [
        new Cartesian2(-size, -size), // 左上角
        new Cartesian2(size, -size), // 右上角
        new Cartesian2(size, size), // 右下角
        new Cartesian2(-size, size), // 左下角
    ]
}

const getMultiPoints = (data) => {
    let points = {};
    data.map((first) => {
        const first_start = first.start.join(',');
        const first_end =  first.end.join(',');

        data.map(two => {
            const two_start = two.start.join(',');
            const two_end =  two.end.join(',');

            let key, value;
            if (first_start === two_start && first_end !== two_end) {
                key = first_start;
                value = [first.end, first.start, two.end];
            } else if (first_start === two_end && first_end !== two_start) {
                key = first_start;
                value = [first.end, first.start, two.start];
            } else if (first_end === two_start && first_start !== two_end) {
                key = first_end;
                value = [first.start, first.end, two.end];
            } else if (first_end === two_end && first_start !== two_start) {
                key = first_end;
                value = [first.start, first.end, two.start];
            }

            if (key && value) {
                if (points[key]) {
                    points[key].push(value);
                } else {
                    points[key] = [value];
                }
            }
        })
    })

    return points;
}

function createGradientImage(canvasId, startColor,endColor) {
    // 获取canvas元素
    let canvas = document.createElement('canvas');

    // 设置canvas的尺寸
    canvas.width = 512;
    canvas.height = 32;

    // 获取绘图上下文
    const ctx = canvas.getContext('2d');

    // 创建一个线性渐变
    // 假设我们想要从左到右的渐变，你也可以根据需要调整渐变的方向
    const gradient = ctx.createLinearGradient(0, 0, 512, 0);

    // 设置渐变的起始颜色和结束颜色
    // 如果startColor是'transparent'，则直接使用，否则你可能需要转换它
    // 这里我们假设startColor已经是有效的CSS颜色字符串，或者你可以添加转换逻辑
    gradient.addColorStop(0, startColor || 'transparent'); // 开始是透明的
    gradient.addColorStop(1, endColor); // 结束是传入的色值

    // 使用渐变填充整个canvas
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    return canvas.toDataURL();
}

class ThreeLineLayer {
    constructor(options) {
        const {show = true} = options;
        this.map = options.map;

        // 图层标识
        this.key = options.key;

        // 图层名称
        this.name = options.name;

        // 标签字段
        this.labelField = options.labelField;

        // 模版内容
        this.template = options.template;

        // 图例
        this.legend = options.legend;

        // 图层控制
        this.layerControl = options.layerControl;

        // 取消气泡
        this.cancelPopup = options.cancelPopup;

        // 气泡模板
        this.tipTemplate = options.tipTemplate;

        // 仅展示
        this.viewOnly = options.viewOnly;

        this.data = options.data;

        this.color = options.color;

        this.image = options.image;

        this.primitives = [];

        this.show = show;

        if (this.data?.length) {
            this.loadData(this.data)
        }
    }

    set show(value) {
        if (this.primitives?.length) {
            this.primitives.map(primitive => {
                primitive.show = value;
            })
        }
    }

    loadData(data) {
        this.data = data;
        const map = this.map;
        const show = this.show;

        this.destroy();

        this.primitives = [];

        const colors = {};
        const images = {};

        // getMultiPoints(data);

        data.map((item) => {
            let {start, end, radius} = item;

            let positions = item.data ? [...item.data.flat(Infinity)] :
                [
                    start[0], start[1], start[2] || 0,
                    end[0], end[1], end[2] || 0,
                ];

            let color_str = item.color || this.color || '#0080ff';
            let image_str = item.image || this.image;

            if (image_str) {
                // 缓存颜色
                images[image_str] = images[image_str] || {
                    item,
                    line_instances: [],
                    data: [],
                    ima: image_str
                };

                // 缓存颜色对应的数据
                images[image_str].line_instances.push(new GeometryInstance({
                    geometry: new PolylineVolumeGeometry({
                        polylinePositions: Cartesian3.fromDegreesArrayHeights(positions),
                        vertexFormat : VertexFormat.DEFAULT,
                        shapePositions : computeCircle(radius || 1.0)
                    }),
                    id: item.id
                }))

                images[image_str].data.push(item)
            } else {
                // 缓存颜色
                colors[color_str] = colors[color_str] || {
                    item,
                    line_instances: [],
                    data: [],
                    color: color_str
                };

                // 缓存颜色对应的数据
                colors[color_str].line_instances.push(new GeometryInstance({
                    geometry: new PolylineVolumeGeometry({
                        polylinePositions: Cartesian3.fromDegreesArrayHeights(positions),
                        vertexFormat : VertexFormat.DEFAULT,
                        shapePositions : computeCircle(radius || 1.0)
                    }),
                    id: item.id
                }))

                colors[color_str].data.push(item)
            }
        });

        for (let image in images) {
            const {ima, line_instances, data} = images[image];

            const line = new Primitive({
                geometryInstances: line_instances,
                show,
                appearance: new MaterialAppearance({
                    flat: false,
                    material: new Material({
                        strict: false,
                        fabric: {
                            materials : {
                                diffuseMaterial : {
                                    type : 'DiffuseMap',
                                    uniforms : {
                                        image : ima,
                                        repeat: new Cartesian2(10.0, 2.0) // 设置水平和垂直方向上的重复次数
                                    }
                                },
                            },
                            components : {
                                diffuse : 'diffuseMaterial.diffuse',
                            },
                        },
                        minificationFilter: TextureMinificationFilter.LINEAR,
                        magnificationFilter: TextureMagnificationFilter.LINEAR,
                    })
                }),
                modelMatrix: Matrix4.IDENTITY,
                vertexCacheOptimize: false,
                interleave: false,
                compressVertices: true,
                releaseGeometryInstances: true,
                allowPicking: true,
                cull: true,
                asynchronous: true,
                debugShowBoundingVolume: false,
                shadows: ShadowMode.DISABLED
            })
            this.setAttribute(line, data);
            map.scene.primitives.add(line);
            this.primitives.push(line);
        }

        for (let color_str in colors) {
            const {color, line_instances, startColor, endColor, data} = colors[color_str];
            // 颜色转换
            const _startColor = startColor || transformRgb(color, 0.2);
            const _endColor = endColor || transformRgb(color, 1);

            const image = createGradientImage(color_str, _startColor, _endColor);

            const line = new Primitive({
                geometryInstances: line_instances,
                show,
                appearance: new MaterialAppearance({
                    flat: false,
                    material: new Material({
                        strict: false,
                        fabric: {
                            uniforms: {
                                image,
                                speed: 5,
                                repeat:  new Cartesian2(1.0, 0.0) // 设置水平和垂直方向上的重复次数
                            },
                            source: glsl,
                        },
                        translucent: function() {
                            return true;
                        },
                        minificationFilter: TextureMinificationFilter.LINEAR,
                        magnificationFilter: TextureMagnificationFilter.LINEAR,
                    })
                }),
                modelMatrix: Matrix4.IDENTITY,
                vertexCacheOptimize: false,
                interleave: false,
                compressVertices: true,
                releaseGeometryInstances: true,
                allowPicking: true,
                cull: true,
                asynchronous: true,
                debugShowBoundingVolume: false,
                shadows: ShadowMode.DISABLED
            })
            this.setAttribute(line, data);
            map.scene.primitives.add(line);
            this.primitives.push(line);
        }

        this.loaded?.(this.primitives);
    }

    setAttribute(primitive, data) {
        // 图层标识
        primitive.key = this.key;

        // 数据
        primitive.data = data;

        // 图层名称
        primitive.name = this.name;

        // 标签字段
        primitive.labelField = this.labelField;

        // 模版内容
        primitive.template = this.template;

        // 图例
        primitive.legend = this.legend;

        // 图层控制
        primitive.layerControl = this.layerControl;

        // 取消气泡
        primitive.cancelPopup = this.cancelPopup;

        // 气泡模板
        primitive.tipTemplate = this.tipTemplate;

        // 仅展示
        primitive.viewOnly = this.viewOnly;
    }

    destroy() {
        const map = this.map;

        if (this.primitives?.length) {
            this.primitives.map(primitive => {
                map.scene.primitives.remove(primitive);
            })
            this.primitives = null;
        }
    }
}

export default ThreeLineLayer;
